import { Stripe, StripeElementStyle } from "@stripe/stripe-js";
import { DeepMap, FieldError } from "react-hook-form";
import { StripeFormDetails } from "..";
import { InputTypeName } from "../../../../../const";
import {
  AgregatedProcessPaymentResponse,
  ProcessPaymentStripeRequestData,
} from "../../../types";
import { cardStyles } from "./stripe-elements.styles";
import { StyleType } from "../../../../../types";

export type StripeSetupItemTypeName =
  | "cardNumber"
  | "expirationDate"
  | "cvc"
  | "postal_code"
  | "city"
  | "state"
  | "line1"
  | "line2"
  | "name"
  | "email"
  | "phone"
  | "country"
  | "saveInWallet";

export type SetupItemTypeInternal<ValueType> = {
  /**
   * Property to set a row index to the specific element
   */
  row: number;
  /**
   * Title for specific input
   */
  label: string;
  /**
   * Name for specific input
   */
  name?: StripeSetupItemTypeName;
  /**
   * The className to be used for set styles for non stripe elements
   */
  className?: string;
  /**
   * Property for set if input is required or not
   */
  required?: boolean | string;
  /**
   * The styles property for stripe elements input
   */
  styles?: StripeElementStyle;
  /**
   * Property for set default input value
   */
  defaultValue?: ValueType;
  /**
   * Validation message to be used after getting validation error
   */
  validationMessage?: string;
  /**
   * Validation callback function to validate input
   */
  validate?: (value: ValueType) => boolean;
  /**
   * Property for set a used input type
   */
  inputType?: InputTypeName;
  /**
   * Property for set an input's placeholder
   */
  placeholder?: string;
  /**
   * Property specifies an input making pattern as a string
   */
  mask?: string;
};

export type SetupItemType<ValueType> = ValueType extends string
  ? SetupItemTypeInternal<string>
  : SetupItemTypeInternal<boolean>;

/**
 * StripeSetupType
 */
export type StripeSetupType = {
  /**
   * Object represents cardNumber stripe input
   */
  cardNumber: SetupItemType<string>;
  /**
   * Object represents expirationDate stripe input
   */
  expirationDate: SetupItemType<string>;
  /**
   * Object represents cvc stripe input
   */
  cvc?: SetupItemType<string>;
  /**
   * Object represents postal_code input
   */
  postal_code?: Exclude<SetupItemType<string>, "required">;
  /**
   * Object represents city input
   */
  city?: SetupItemType<string>;
  /**
   * Object represents state input
   */
  state?: SetupItemType<string>;
  /**
   * Object represents line1 input
   */
  line1?: SetupItemType<string>;
  /**
   * Object represents line2 input
   */
  line2?: SetupItemType<string>;
  /**
   * Object represents name input
   */
  name?: SetupItemType<string>;
  /**
   * Object represents email input
   */
  email?: SetupItemType<string>;
  /**
   * Object represents phone input
   */
  phone?: SetupItemType<string>;
  /**
   * Object represents country checkbox input
   */
  country?: SetupItemType<string>;
  /**
   * Object represents saveInWallet checkbox input
   */
  saveInWallet?: SetupItemType<boolean>;
};

export type InputName = keyof StripeSetupType;

export const InitialSetupItem: SetupItemType<string> = {
  row: -1,
  label: "",
};

export const CardNumberDefaultLabels: Record<StyleType, string> = {
  default: "Card Number",
  AHSRebrand: "Card number",
  HBW210: "Card number",
};

export const ExpirationDateDefaultLabels: Record<StyleType, string> = {
  default: "Expiration Date",
  AHSRebrand: "Expiration date",
  HBW210: "Expiration date",
};
export const StripeDefaultSetup: StripeSetupType = {
  cardNumber: {
    row: 0,
    label: CardNumberDefaultLabels["default"],
    styles: cardStyles["default"],
    className: "sm:col-start-1 sm:col-span-4 col-start-1 col-end-7 sm:pr-4",
  },
  expirationDate: {
    row: 0,
    label: ExpirationDateDefaultLabels["default"],
    className: "sm:col-span-2 sm:col-end-7 col-start-1 col-span-7",
    styles: cardStyles["default"],
  },
};

export type StripeSetup = {
  PublicKey: string;
  SetupIntent: Record<string, unknown>;
};

export type InternalStripeClient = {
  client: Stripe | null;
  processPayment: () => void;
};

export type HandleStripeFormErrors = (
  err: DeepMap<StripeFormDetails, FieldError>,
  resolveStripePayment: (
    value:
      | AgregatedProcessPaymentResponse
      | PromiseLike<AgregatedProcessPaymentResponse>
  ) => void
) => void;

export type SubmitCardSetup = (
  requestData: ProcessPaymentStripeRequestData | undefined,
  formData: StripeFormDetails,
  clientSecret: string,
  registrationID: string
) => Promise<AgregatedProcessPaymentResponse>;

export type SubmitCardSetupWithRedisErrorHandler = SubmitCardSetup;
export const Placeholders: Partial<Record<keyof StripeSetupType, string>> = {
  postal_code: "99999",
  email: "example@email.com",
  phone: "999-999-9999",
};

export type CardStylesType = Record<StyleType, StripeElementStyle>;
